import React, { useState } from 'react';
import { QuadTab } from '../QuadTab';
import { PromptSettingsGear } from '../PromptSettingsGear';
import { AccountIntelSummaryView } from './AccountIntelSummaryView';
import { AccountIntelAllView } from './AccountIntelAllView';

/**
 * Account Intelligence panel shell — a Summary/All toggle wrapping the two
 * views split out of what used to be this file's own body (plan
 * `plans/2026-09-23-002-rep-prompt-overrides-and-account-intelligence-summary-plan.md`
 * U8, R16, decision 9). "Summary" is the Terra-side AI digest of the local
 * `account_intelligence` mirror row (`AccountIntelSummaryView`); "All" is the
 * unchanged, full live-Keystone treatment (`AccountIntelAllView`) — moved
 * verbatim, so the live-Keystone fetch now only fires once a rep clicks
 * "All" instead of on every mount.
 *
 * Mounted in four places, all of which get the toggle for free by sharing
 * this one shell:
 *   1. Command-center `ContactDetail` (`ContactDetailPanels.tsx`, `surface='command_center'`)
 *   2. `AccountDrillDown` roster rows, via the same `ContactDetail`
 *   3. `account-view/ContactTab`, via the same `ContactDetail`
 *   4. `AccountDrillDown`'s own right-quad Intelligence tab (direct mount)
 *
 * The gear that opens the rep's prompt override (`/library?tab=prompts`)
 * only makes sense next to the Summary tab — the account panel isn't
 * wrapped in a `FlowDetailPanel` at all four mount points (the drill-down's
 * right quad mounts it directly), so unlike the Contact panel's
 * `FlowDetailPanel.headerAction` gear, this one lives inside the toggle
 * strip itself (Key Technical Decision 13) and is absent on the All tab.
 */
export function AccountIntelPanel({ accountId }: { accountId: string | null }) {
  const [tab, setTab] = useState<'summary' | 'all'>('summary');

  return (
    <div>
      <div className="flex items-center gap-1 border-b border-secondary dark:border-nightshade-700 mb-2">
        <QuadTab label="Summary" active={tab === 'summary'} onClick={() => setTab('summary')} />
        <QuadTab label="All" active={tab === 'all'} onClick={() => setTab('all')} />
        <div className="ml-auto">
          {tab === 'summary' && <PromptSettingsGear ariaLabel="Edit Account Intelligence prompt" />}
        </div>
      </div>
      {tab === 'summary' ? <AccountIntelSummaryView accountId={accountId} /> : <AccountIntelAllView accountId={accountId} />}
    </div>
  );
}
